// Laser kiosk: the cut sheet as it comes off the laser, each piece coloured by the pallet / rack it goes to.
// The map is jobq-api GET /cut_sheets/{id}/nest_map (read from the Amada Sheet Report PDF); which piece goes
// where is features/laserKiosk/sheetMap.js. A sheet with no map shows nothing.
import React, { useEffect, useMemo, useState } from "react";
import config from "../../config";
import { assignSheet, defaultSheet, lastSheet, mapTrust, sheetLabel } from "../../features/laserKiosk/sheetMap";

// cut_sheet_id -> {body | null (none), at}. The kiosk runs for days: an answer is kept 10 minutes (a map added or
// replaced since shows up within that), and a failed read is never kept.
const mapCache = new Map();
export const MAP_CACHE_MS = 10 * 60 * 1000;

export function clearNestMapCache() {
  mapCache.clear();
}

export async function loadNestMap(cutSheetId, now = Date.now()) {
  const hit = mapCache.get(cutSheetId);
  if (hit && now - hit.at < MAP_CACHE_MS) return hit.body;
  try {
    const res = await fetch(`${config.API_BASE_URL}/cut_sheets/${cutSheetId}/nest_map`);
    if (res.status === 404) {
      mapCache.set(cutSheetId, { body: null, at: now });
      return null;
    }
    if (!res.ok) return undefined;     // not kept: asked again next time
    const body = await res.json();
    const ok = body && body.map ? body : null;
    mapCache.set(cutSheetId, { body: ok, at: now });
    return ok;
  } catch (e) {
    return undefined;
  }
}

const STRIPE = "sm-stripe";
const CROSS = "sm-cross";

export function SheetSvg({ map, pieces, highlight, onPick, height, colour = true, numbers = false }) {
  const w = Number(map.sheet_x) || 1;
  const h = Number(map.sheet_y) || 1;
  const stroke = Math.max(w, h) / 600;
  const u = Math.max(w, h) / 120;       // pattern / text unit: an inch on a 120" sheet
  return (
    <svg
      data-testid="sheet-map-svg"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height: height || "auto", background: "#3a3f4b", borderRadius: 6, display: "block" }}
      role="img"
      aria-label={`Sheet ${w} x ${h} in, ${pieces.length} pieces`}
    >
      <defs>
        <pattern id={STRIPE} patternUnits="userSpaceOnUse" width={u * 2} height={u * 2} patternTransform="rotate(45)">
          <rect width={u} height={u * 2} fill="rgba(0,0,0,0.45)" />
        </pattern>
        <pattern id={CROSS} patternUnits="userSpaceOnUse" width={u * 2} height={u * 2}>
          <path d={`M0 0 L${u * 2} ${u * 2} M${u * 2} 0 L0 ${u * 2}`} stroke="#fff" strokeWidth={u / 3} />
        </pattern>
      </defs>
      <rect x={0} y={0} width={w} height={h} fill="#454b58" stroke="#8a93a3" strokeWidth={stroke * 2} />
      {pieces.map((p) => {
        const dim = highlight && highlight !== p.key;
        const fill = colour ? p.color : "#8a93a3";
        const props = {
          fill,
          fillOpacity: dim ? 0.18 : 0.95,
          stroke: "#111",
          strokeWidth: stroke,
          onClick: onPick ? () => onPick(p.key) : undefined,
          style: onPick ? { cursor: "pointer" } : undefined,
        };
        const pat = colour && p.pattern ? `url(#${p.pattern === "cross" ? CROSS : STRIPE})` : null;
        return (
          <g key={p.i} opacity={dim ? 0.35 : 1}>
            {p.d
              ? <path {...props} d={p.d} fillRule="evenodd"><title>{p.product_id}</title></path>
              : <circle {...props} cx={p.cx} cy={p.cy} r={u}><title>{p.product_id}</title></circle>}
            {pat && (p.d
              ? <path d={p.d} fillRule="evenodd" fill={pat} pointerEvents="none" />
              : <circle cx={p.cx} cy={p.cy} r={u} fill={pat} pointerEvents="none" />)}
            {numbers && colour && p.seq != null && (
              <text x={p.cx} y={p.cy} fontSize={u * 2.4} fontWeight="800" textAnchor="middle" dominantBaseline="central"
                fill="#fff" stroke="#000" strokeWidth={u / 4} paintOrder="stroke" pointerEvents="none">{p.seq}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Swatch({ e, size }) {
  const bg = e.pattern === "cross"
    ? `repeating-linear-gradient(45deg, ${e.color} 0 4px, #fff 4px 6px), ${e.color}`
    : e.pattern === "stripe" ? `repeating-linear-gradient(45deg, ${e.color} 0 4px, rgba(0,0,0,0.45) 4px 7px)` : e.color;
  return <span style={{ width: size, height: size, borderRadius: 4, background: bg, flexShrink: 0, border: "1px solid #111" }} />;
}

function Legend({ legend, highlight, onPick, big }) {
  return (
    <div data-testid="sheet-map-legend" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {legend.map((e) => (
        <button
          key={e.key}
          type="button"
          onClick={() => onPick(highlight === e.key ? null : e.key)}
          style={{
            display: "flex", alignItems: "center", gap: 8, textAlign: "left",
            background: highlight === e.key ? "#2b3446" : "transparent",
            border: `2px solid ${highlight === e.key ? "#8ec5ff" : "transparent"}`, borderRadius: 6,
            color: e.blocked ? "#ff9a9a" : "#e8eef6", padding: "2px 6px", cursor: "pointer",
            fontSize: big ? "1.3rem" : "0.95rem",
          }}
        >
          <Swatch e={e} size={big ? 26 : 18} />
          {e.seq != null && <b style={{ fontFamily: "monospace" }}>{e.seq} of {e.of}</b>}
          <span style={{ fontWeight: 700 }}>{e.label}</span>
          {e.beacon && <span style={{ fontFamily: "monospace", color: "#7ede9e" }}>{e.beacon}</span>}
          {e.blocked && <span style={{ fontWeight: 800 }}>DO NOT ADD</span>}
          <span style={{ marginLeft: "auto", fontFamily: "monospace", fontWeight: 800 }}>{e.count} pcs</span>
        </button>
      ))}
    </div>
  );
}

export default function SheetMapPanel({ task, parts }) {
  const cutSheetId = task?.cut_sheet_id || null;
  const [body, setBody] = useState(undefined);       // undefined = loading / failed, null = none
  const [sheet, setSheet] = useState(() => defaultSheet(task));
  const [highlight, setHighlight] = useState(null);
  const [big, setBig] = useState(false);

  // read on a new cut sheet, and again each 10 minutes (the kiosk payload re-renders this every few seconds)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), MAP_CACHE_MS);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    let live = true;
    if (!cutSheetId) { setBody(null); return undefined; }
    loadNestMap(cutSheetId).then((b) => { if (live && b !== undefined) setBody(b); });
    return () => { live = false; };
  }, [cutSheetId, tick]);
  useEffect(() => { setBody(undefined); setHighlight(null); }, [cutSheetId]);

  // follow the laser: a new sheet recorded moves the view to it
  const recorded = defaultSheet(task);
  useEffect(() => { setSheet(recorded); }, [recorded, task?.task_id]);

  const map = body ? body.map : null;
  const res = useMemo(
    () => (map ? assignSheet(map, parts, sheet, { sheetsPlanned: task?.sheet_required }) : null),
    [map, parts, sheet, task?.sheet_required]);
  if (!map || !res) return null;
  const trust = mapTrust(body);
  const sheets = lastSheet(task);
  const numbers = res.pieces.length <= 150;

  const stepper = (
    <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#c9d4e2", fontWeight: 700 }}>
      <button type="button" aria-label="Previous sheet" disabled={sheet <= 1} onClick={() => setSheet(sheet - 1)}
        style={stepBtn}>◀</button>
      <span data-testid="sheet-map-sheet">{sheetLabel(sheet, task)}</span>
      <button type="button" aria-label="Next sheet" disabled={sheet >= sheets} onClick={() => setSheet(sheet + 1)}
        style={stepBtn}>▶</button>
    </div>
  );
  const content = (isBig) => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ color: "#9aa4b4", fontWeight: 800, fontSize: isBig ? "1.3rem" : "0.9rem" }}>
          WHERE EACH PIECE GOES
        </span>
        <span style={{ marginLeft: "auto" }}>{stepper}</span>
      </div>
      {!trust.colour && (
        <div data-testid="sheet-map-untrusted" role="alert"
          style={{ background: "#5a4500", color: "#ffe9a3", borderRadius: 8, padding: "0.35rem 0.5rem",
                   fontWeight: 800, fontSize: isBig ? "1.3rem" : "1rem" }}>
          Pieces not coloured: {trust.why}. Use the destination cards.
        </div>
      )}
      {trust.colour && (res.short.length > 0 || res.unchecked) && (
        <div data-testid="sheet-map-short" style={{ color: "#ffd969", fontWeight: 700 }}>
          {res.unchecked
            ? "No planned sheet count: the colours can't be checked against it."
            : res.short.map((x) => `${x.product}: ${x.by} fewer pallet places than pieces cut`).join("; ")
              + ". Colours near a pallet change may be off by that many."}
        </div>
      )}
      {trust.colour && res.cantPlace.length > 0 && (
        <div data-testid="sheet-map-cant-place" style={{ color: "#ffd969", fontWeight: 700 }}>
          {res.cantPlace.join(", ")}: the job has more of these than its sheets make, so they aren't placed. Use the cards.
        </div>
      )}
      <SheetSvg map={map} pieces={res.pieces} highlight={trust.colour ? highlight : null} colour={trust.colour}
        numbers={numbers} onPick={trust.colour ? (k) => setHighlight(highlight === k ? null : k) : undefined}
        height={isBig ? "62vh" : undefined} />
      {trust.colour && <Legend legend={res.legend} highlight={highlight} onPick={setHighlight} big={isBig} />}
    </>
  );
  return (
    <div data-testid="sheet-map-panel"
      style={{ background: "#1a1d24", border: "2px solid #2a2f3a", borderRadius: 12, padding: "0.5rem",
               display: "flex", flexDirection: "column", gap: 6 }}>
      {content(false)}
      <button type="button" onClick={() => setBig(true)}
        style={{ ...stepBtn, alignSelf: "flex-start", padding: "0.1rem 0.6rem" }}>Bigger</button>
      {big && (
        <div role="dialog" aria-modal="true" aria-label="Sheet map"
          onKeyDown={(e) => { if (e.key === "Escape") setBig(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1060, display: "flex",
                   alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setBig(false); }}>
          <div style={{ width: "92vw", maxHeight: "94vh", overflowY: "auto", background: "#1a1d24", borderRadius: 12,
                        padding: "1rem", display: "flex", flexDirection: "column", gap: 10 }}>
            {content(true)}
            <button type="button" autoFocus onClick={() => setBig(false)}
              style={{ ...stepBtn, alignSelf: "flex-end", fontSize: "1.2rem", padding: "0.3rem 1.2rem" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

const stepBtn = {
  background: "#2a2f3a", color: "#bcd4ff", border: "2px solid #3a4050", borderRadius: 8,
  fontWeight: 700, cursor: "pointer", padding: "0 0.5rem",
};
